import React, { FC, ChangeEvent } from 'react';
import { Divider, Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { makeStyles } from '@material-ui/core/styles';

import { Capability, PaddedPaper } from 'components';
import { GroupContentApprovalType, UpdateGroupInput } from 'api';
import { useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';
import { useGroupData } from 'features/groups/hooks';

import GroupSwitch from './GroupSwitch';
import { getGroupHexColor } from '../../helpers';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(4, 4, 5, 3),
  },
}));

const GroupSettings: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<UpdateGroupInput>();
  const { groupColors } = useGroupData();
  const currentUser = useCurrentUser();

  const hexColor = getGroupHexColor(groupColors, values.color);
  const classes = useStyles({ hexColor });
  const isAdmin = isAdminUser(currentUser);

  function handleApproveToggle(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    const nextValue = checked ? GroupContentApprovalType.Auto : GroupContentApprovalType.Review;

    setFieldValue('content_approval_type', nextValue);
  }

  return (
    <PaddedPaper square className={classes.paper}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={3}>
          <Typography variant="subtitle2">{t('admin:settings')}</Typography>
        </Grid>

        <Grid item xs>
          <Grid container direction="column" spacing={2}>
            <GroupSwitch
              value="allow_user_posts"
              title={t('groups:allowMemberPost')}
              description={t('groups:allowMemberPostDesc')}
            />

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Capability name="Group.ForApproval.Tab.Access">
              <GroupSwitch
                value="content_approval_type"
                title={t('groups:autoApprove')}
                description={t('groups:autoApproveDesc')}
                onChange={handleApproveToggle}
                checkedFn={value => value === GroupContentApprovalType.Auto}
              />

              <Grid item xs={12}>
                <Divider light />
              </Grid>
            </Capability>

            <GroupSwitch
              value="read_only"
              title={t('groups:readOnly')}
              description={t('groups:readOnlyDesc')}
            />

            {isAdmin && (
              <Capability name="Group.Function.PrivateGroups">
                <Grid item xs={12}>
                  <Divider light />
                </Grid>

                <GroupSwitch
                  value="is_private"
                  title={t('groups:privateGroup')}
                  description={t('groups:privateGroupDesc')}
                />
              </Capability>
            )}
          </Grid>
        </Grid>
      </Grid>
    </PaddedPaper>
  );
};

export default GroupSettings;
